import React, { useMemo, useRef, useCallback, useContext } from 'react';
import { StreamPlayerWrapper as StreamPlayer, MutedPreviewOverlay } from '@bizzabo/broadcast';
import { VolumeMuteOutlineIcon } from '@bizzabo/icons';
import config from '../../../../config/config';
import backToAgenda from '../../../../utils/backToAgendaAction';
import { AgendaContext } from '../AgendaContextProvider';
import FeatureToggleUtils from 'FeatureToggleUtils';
import ReactionsOverlay from './ReactionsOverlay';
import useCaptionHub from './useCaptionHub';

const StreamState = {
    NOT_STARTED: 'NOT_STARTED',
    STARTED: 'STARTED',
    ENDED: 'ENDED',
    ERROR: 'ERROR'
};

const END_SESSION_ACTION_TEXT = 'Go to agenda';

function StreamPlayerWrapper(
    {
        streamUrl,
        providerType,
        session,
        user,
        providerDataOptions,
        overridePlayerConfigs,
        eventId,
        captionsEnabled,
        turnOnCaptionsForAttendees,
        captionsUrl = null,
        socketConfig = {},
        streamId,
        isMobileView = false,
        muxPlayerProps,
        onStateChanged
    }
) {
    const context = useContext(AgendaContext);

    const backToAgendaHandler = useCallback(() => {
        if (!context) {
            return;
        }
        const { isWidget, serverRendered, event } = context;
        backToAgenda({ isWidget, serverRendered, event });
    }, [context]);

    const endStreamButton = (
        <button
            className="text-uppercase bz-button font-size-13 custom-color color-1-hover-opacity color-1-background"
            onClick={backToAgendaHandler}
        >{END_SESSION_ACTION_TEXT}</button>
    );

    const versionToScriptUrls = {
        plyr: FeatureToggleUtils.getFeatureValue('bap-plyr-io-version'),
        hls: FeatureToggleUtils.getFeatureValue('bap-hls-js-version'),
        mux: FeatureToggleUtils.getFeatureValue('bap-mux-player-version'),
    };
    const bizzaboAgnosticPlayer = FeatureToggleUtils.getFeatureValue('bizzabo-agnostic-player');
    const isMuxPlayer = useMemo(() => bizzaboAgnosticPlayer === 'mux', [bizzaboAgnosticPlayer]);
    const containerRef = useRef(null);

    useCaptionHub({
        enabled: isMuxPlayer && captionsEnabled,
        captionsUrl,
        containerRef,
    });
    const providerData = {
        ...providerDataOptions,
        sub_property_id: eventId,
        player_name: isMuxPlayer ? 'Mux' : 'Plyr',
        player_version: isMuxPlayer
            ? versionToScriptUrls.mux
            : `plyr: ${versionToScriptUrls.plyr}; hls: ${versionToScriptUrls.hls}`,
    };
    const aggressiveRestartMode = FeatureToggleUtils.isFeatureEnabled('realtime-notifier-aggressive-mode');
    const enableCurtainMechanism = FeatureToggleUtils.isFeatureEnabled('rollout-virtual-enable-curtain-mechanism');
    const streamSocketConfig = isMobileView ? {} : {
        accountId: socketConfig?.accountId,
        eventId: String(eventId),
        apiUrl: `${config.apiServer}/`,
        realtimeNotificationsHost: config.realtimeNotificationsHost,
        accessTokenCookieName: socketConfig?.tokenCookieName,
        refreshTokenUrl: socketConfig?.refreshTokenUrl,
        aggressiveRestartMode,
        streamId: streamId,
        sessionId: session.id
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <StreamPlayer
                streamUrl={streamUrl}
                providerType={providerType}
                session={session}
                userId={user.id}
                userEmail={user.primaryEmail}
                providerDataOptions={providerData}
                overridePlayerConfigs={overridePlayerConfigs}
                config={config}
                rootClassName={'stream-video-wrapper'}
                noStreamClassName={'fill-container no-stream'}
                endStreamButton={endStreamButton}
                versionToScriptUrls={versionToScriptUrls}
                bizzaboAgnosticPlayer={bizzaboAgnosticPlayer}
                sessionType='live'
                mutedPreviewComponent={
                    <MutedPreviewOverlay IconComponent={VolumeMuteOutlineIcon} />
                }
                captionsEnabled={captionsEnabled}
                showCaptionsByDefault={captionsEnabled ? turnOnCaptionsForAttendees : false}
                socketConfig={streamSocketConfig}
                enableStreamStatusNotifier={!isMobileView && enableCurtainMechanism}
                onStreamStateChanged={onStateChanged}
                muxPlayerProps={muxPlayerProps}
            />
            <ReactionsOverlay activity={session} />
        </div>
    );
}

const NoStreamTexts = {
    [StreamState.NOT_STARTED]: {
        title: 'Session starting soon',
        text: 'The live session will begin here shortly'
    },
    [StreamState.ENDED]: {
        title: 'Session has ended',
        text: 'To explore more content, check out the Agenda'
    },
    [StreamState.ERROR]: {
        title: 'Error loading session',
        text: 'Try to refresh the page'
    }
};

export default React.memo(StreamPlayerWrapper);
